import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { postDataAPI } from './fetchData';

// Global City/State/Province auto-fill data
const cityStateData = {
    // United States
    "New York": "NY", "Los Angeles": "CA", "Chicago": "IL", "Houston": "TX", "Phoenix": "AZ",
    "Philadelphia": "PA", "San Antonio": "TX", "San Diego": "CA", "Dallas": "TX", "San Jose": "CA",
    "Austin": "TX", "Jacksonville": "FL", "Fort Worth": "TX", "Columbus": "OH", "Charlotte": "NC",
    "San Francisco": "CA", "Indianapolis": "IN", "Seattle": "WA", "Denver": "CO", "Washington": "DC",
    "Boston": "MA", "El Paso": "TX", "Nashville": "TN", "Detroit": "MI", "Oklahoma City": "OK",
    "Portland": "OR", "Las Vegas": "NV", "Memphis": "TN", "Louisville": "KY", "Baltimore": "MD",
    "Milwaukee": "WI", "Albuquerque": "NM", "Tucson": "AZ", "Fresno": "CA", "Sacramento": "CA",
    "Mesa": "AZ", "Kansas City": "MO", "Atlanta": "GA", "Long Beach": "CA", "Colorado Springs": "CO",
    "Raleigh": "NC", "Miami": "FL", "Virginia Beach": "VA", "Omaha": "NE", "Oakland": "CA",
    "Minneapolis": "MN", "Tulsa": "OK", "Arlington": "TX", "Tampa": "FL", "New Orleans": "LA",
    
    // Canada
    "Toronto": "ON", "Montreal": "QC", "Vancouver": "BC", "Calgary": "AB", "Edmonton": "AB",
    "Ottawa": "ON", "Winnipeg": "MB", "Quebec City": "QC", "Hamilton": "ON", "Kitchener": "ON",
    "London (ON)": "ON", "Victoria": "BC", "Halifax": "NS", "Oshawa": "ON", "Windsor": "ON",
    
    // United Kingdom
    "London": "England", "Birmingham": "England", "Manchester": "England", "Glasgow": "Scotland",
    "Liverpool": "England", "Edinburgh": "Scotland", "Leeds": "England", "Sheffield": "England",
    "Bristol": "England", "Cardiff": "Wales", "Belfast": "Northern Ireland", "Newcastle (UK)": "England",
    "Nottingham": "England", "Portsmouth": "England", "Plymouth": "England",
    
    // Australia
    "Sydney": "NSW", "Melbourne": "VIC", "Brisbane": "QLD", "Perth": "WA", "Adelaide": "SA",
    "Gold Coast": "QLD", "Newcastle (AU)": "NSW", "Canberra": "ACT", "Sunshine Coast": "QLD",
    "Wollongong": "NSW", "Hobart": "TAS", "Geelong": "VIC", "Townsville": "QLD",
    
    // Germany
    "Berlin": "Berlin", "Hamburg": "Hamburg", "Munich": "Bavaria", "Cologne": "North Rhine-Westphalia",
    "Frankfurt": "Hesse", "Stuttgart": "Baden-Württemberg", "Düsseldorf": "North Rhine-Westphalia",
    "Dortmund": "North Rhine-Westphalia", "Essen": "North Rhine-Westphalia", "Leipzig": "Saxony",
    "Bremen": "Bremen", "Dresden": "Saxony", "Hanover": "Lower Saxony"
};

// Helper function to process Excel dates
const processExcelDate = (value) => {
    if (!value) return null;
    
    // Handle Excel date serial numbers
    if (!isNaN(value) && value > 40000 && value < 60000) {
        try {
            const excelDate = XLSX.SSF.parse_date_code(value);
            if (excelDate) {
                return `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
            }
        } catch (e) {
            console.warn('Failed to parse Excel date serial:', value);
        }
    }
    
    // Handle date strings
    if (typeof value === 'string') {
        // Try different date formats
        const dateFormats = [
            // MM.DD.YYYY format (like 09.18.2025)
            /^(\d{1,2})\.(\d{1,2})\.(\d{4})/,
            // MM/DD/YYYY format
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})/,
            // DD-MM-YYYY format
            /^(\d{1,2})-(\d{1,2})-(\d{4})/,
            // YYYY-MM-DD format
            /^(\d{4})-(\d{1,2})-(\d{1,2})/
        ];
        
        for (const format of dateFormats) {
            const match = value.match(format);
            if (match) {
                let year, month, day;
                
                if (format.source.startsWith('^(\\d{4})')) {
                    // YYYY-MM-DD format
                    [, year, month, day] = match;
                } else {
                    // MM.DD.YYYY, MM/DD/YYYY, DD-MM-YYYY formats
                    [, month, day, year] = match;
                }
                
                // Ensure proper formatting
                month = String(month).padStart(2, '0');
                day = String(day).padStart(2, '0');
                
                // Validate date
                const date = new Date(year, month - 1, day);
                if (date.getFullYear() == year && date.getMonth() == month - 1 && date.getDate() == day) {
                    return `${year}-${month}-${day}`;
                }
            }
        }
        
        // Fallback: try standard Date parsing
        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString().split('T')[0];
        }
    }
    
    return null;
};

// Field mapping for bookings CSV/Excel columns to Dizee fields
const bookingFieldMapping = {
    // Date fields (including Bandsintown format)
    'PERF.DATE': 'perf_date',
    'PERF DATE': 'perf_date',
    'Performance Date': 'perf_date',
    'Show Date': 'perf_date',
    'Date': 'perf_date',
    'Start Date* (yyyy-mm-dd)': 'perf_date',
    'Start Date': 'perf_date',
    'Start Date*': 'perf_date',
    'Event Date': 'perf_date',
    'ANNOUNCE': 'announce_date',
    'Announce Date': 'announce_date',
    'On-Sale Date': 'on_sale_date',
    'ON SALE': 'on_sale_date',
    'On Sale Date': 'on_sale_date',
    'End Date': 'end_date',
    
    // Time fields
    'Start Time* (HH:MM)': 'show_time',
    'Start Time': 'show_time',
    'Show Time': 'show_time',
    'Set Time': 'show_time',
    'End Time': 'end_time',
    'On-Sale Time': 'on_sale_time',
    'Schedule Time': 'schedule_time',
    
    // Basic info fields
    'ARTIST': 'artist',
    'Artist': 'artist',
    'Artist Name': 'artist',
    'STATUS': 'status',
    'Status': 'status',
    'VENUE': 'venue',
    'Venue': 'venue',
    'Venue*': 'venue',
    'Venue Name': 'venue',
    'PROMOTER': 'promoter',
    'Promoter': 'promoter',
    'CITY/STATE': 'city_state',
    'City/State': 'city_state',
    'City State': 'city_state',
    'Location': 'city_state',
    'CITY': 'city',
    'City': 'city',
    'City*': 'city',
    'STATE': 'state',
    'State': 'state',
    'Province': 'state',
    'Region': 'state',
    'Region*': 'state',
    'Country': 'country',
    'Country*': 'country',
    'Address': 'address',
    
    // Financial fields
    'GUARANTEE': 'guarantee',
    'Guarantee': 'guarantee',
    'WALKOUT POTENTIAL': 'walkout_potential',
    'Walkout Potential': 'walkout_potential',
    'Walkout': 'walkout_potential',
    'DEAL INFO': 'deal_info',
    'Deal Info': 'deal_info',
    'Deal Type': 'deal_type',
    'ARTIST NET': 'artist_net',
    'Artist Net': 'artist_net',
    
    // Capacity and tickets
    'SELLABLE CAP': 'sellable_cap',
    'Sellable Cap': 'sellable_cap',
    'Capacity': 'sellable_cap',
    'CURRENT TICKET COUNT': 'current_ticket_count',
    'Current Ticket Count': 'current_ticket_count',
    'Tickets Sold': 'tix_sold',
    'TIX SOLD': 'tix_sold',
    '% TIX SOLD': 'tix_sold_percent',
    'Percent Sold': 'tix_sold_percent',
    
    // Links and contact
    'TICKET LINK': 'ticket_link',
    'Ticket Link': 'ticket_link',
    'Ticket URL': 'ticket_link',
    'Ticket Type': 'ticket_type',
    'Ticket Link 2': 'ticket_link_2',
    'Ticket Type 2': 'ticket_type_2',
    'Streaming Link': 'streaming_link',
    'PROMOTER CONTACT EMAILS': 'promoter_contact_emails',
    'Promoter Contact Emails': 'promoter_contact_emails',
    'Contact Email': 'promoter_contact_emails',
    'PRODUCTION/SHOW ADVANCE CONTACTS': 'promoter_contact_names',
    'Production Contacts': 'promoter_contact_names',
    'Show Advance Contacts': 'promoter_contact_names',
    
    // Additional fields
    'APPEARING WITH': 'appearing_with',
    'Appearing With': 'appearing_with',
    'Lineup': 'appearing_with',
    'Event Name': 'event_title',
    'Event Display Format': 'event_display_format',
    'Description': 'notes',
    'Do Not Announce': 'do_not_announce',
    'Event Image': 'event_image',
    'Event Id': 'event_id',
    'Setlist': 'setlist',
    'Timezone': 'timezone',
    'Timezone*': 'timezone',
    'Postal Code': 'postal_code',
    'SCHEDULE': 'schedule',
    'Schedule': 'schedule',
    'Schedule Date': 'schedule_date',
    'MERCH': 'merch',
    'Merch': 'merch',
    'Merchandise': 'merch',
    'TICKET PRICES': 'ticket_prices_tiers',
    'Ticket Prices': 'ticket_prices_tiers',
    'Pricing': 'ticket_prices_tiers',
    
    // Contract and business
    'Contract Status': 'contract_status',
    'CONTRACT STATUS': 'contract_status',
    'Notes': 'notes',
    'NOTES': 'notes',
    'Additional Notes': 'notes'
};

// Process a single row with field mapping
const processBookingRow = (row, fieldMapping) => {
    const mappedRow = {};
    
    Object.keys(row).forEach(csvKey => {
        const value = row[csvKey];
        if (value === null || value === undefined || value === '') return;
        
        // Find the mapped field name
        const mappedKey = fieldMapping[csvKey] || csvKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
        
        // Process dates
        if (mappedKey.includes('date') || csvKey.toLowerCase().includes('date')) {
            const processedDate = processExcelDate(value);
            if (processedDate) {
                mappedRow[mappedKey] = processedDate;
            }
        }
        // Process currency fields
        else if (mappedKey.includes('guarantee') || mappedKey.includes('walkout') || mappedKey.includes('net') || mappedKey.includes('potential')) {
            const cleanValue = String(value).replace(/[$,]/g, '');
            if (!isNaN(cleanValue) && cleanValue !== '') {
                mappedRow[mappedKey] = cleanValue;
            } else {
                mappedRow[mappedKey] = String(value);
            }
        }
        // Process number fields
        else if (mappedKey.includes('cap') || mappedKey.includes('count') || mappedKey.includes('sold')) {
            const cleanValue = String(value).replace(/[,]/g, '');
            if (!isNaN(cleanValue) && cleanValue !== '') {
                mappedRow[mappedKey] = parseInt(cleanValue);
            } else {
                mappedRow[mappedKey] = String(value);
            }
        }
        // Process status field
        else if (mappedKey === 'status') {
            const statusValue = String(value).toLowerCase();
            if (statusValue.includes('confirmed') || statusValue.includes('published')) mappedRow[mappedKey] = 'confirmed';
            else if (statusValue.includes('pending') || statusValue.includes('draft')) mappedRow[mappedKey] = 'pending';
            else if (statusValue.includes('announced')) mappedRow[mappedKey] = 'announced';
            else if (statusValue.includes('private')) mappedRow[mappedKey] = 'private';
            else if (statusValue.includes('cancel')) mappedRow[mappedKey] = 'canceled';
            else mappedRow[mappedKey] = 'pending'; // default
        }
        // Regular text fields
        else {
            mappedRow[mappedKey] = String(value);
        }
    });
    
    return mappedRow;
};

// Post-process to handle city/state logic
const postProcessCityState = (processedData) => {
    return processedData.map(row => {
        const processedRow = { ...row };
        
        // Handle city_state field - split into separate city and state
        if (processedRow.city_state && !processedRow.city && !processedRow.state) {
            const cityStateValue = String(processedRow.city_state).trim();
            
            let city = '', state = '';
            
            if (cityStateValue.includes(',')) {
                const parts = cityStateValue.split(',');
                city = parts[0]?.trim() || '';
                state = parts[1]?.trim() || '';
            } else if (cityStateValue.includes(' - ')) {
                const parts = cityStateValue.split(' - ');
                city = parts[0]?.trim() || '';
                state = parts[1]?.trim() || '';
            } else {
                city = cityStateValue;
            }
            
            if (city) {
                processedRow.city = city;
                
                if (cityStateData[city]) {
                    processedRow.state = cityStateData[city];
                } else if (state) {
                    processedRow.state = state;
                }
            }
            
            delete processedRow.city_state;
        }
        
        // Auto-fill state based on city if city exists but state doesn't
        if (processedRow.city && !processedRow.state && cityStateData[processedRow.city]) {
            processedRow.state = cityStateData[processedRow.city];
        }
        
        return processedRow;
    });
};

/**
 * Parse a booking CSV file
 * @param {File} file - The CSV file to parse
 * @returns {Promise<Array>} - Promise that resolves to an array of parsed booking data
 */
export const parseBookingCSV = (file) => {
    return new Promise((resolve, reject) => {
        const fileText = file.text();
        
        fileText.then(text => {
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    console.log('Parsed CSV data:', results.data);
                    
                    // Apply field mapping logic
                    const processedData = results.data.map(row => 
                        processBookingRow(row, bookingFieldMapping)
                    );
                    
                    // Post-process to handle city/state logic
                    const finalProcessedData = postProcessCityState(processedData);
                    
                    console.log('Processed CSV data with field mapping:', finalProcessedData);
                    resolve(finalProcessedData);
                },
                error: (error) => {
                    console.error('CSV parsing error:', error);
                    reject(new Error('Failed to parse CSV file. Please check the file format.'));
                }
            });
        }).catch(error => {
            console.error('Error reading file:', error);
            reject(new Error('Failed to read CSV file.'));
        });
    });
};

/**
 * Parse a catalog CSV file by uploading to backend
 * @param {File} file - The CSV file to parse
 * @returns {Promise<Array>} - Promise that resolves to an array of parsed catalog data
 */
export const parseCatalogCSV = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('dizeeToken');
    
    try {
        const res = await postDataAPI('aws/csvUploadCatalog', formData, token, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 30000,
        });
        
        console.log('Catalog upload response:', res.data);
        
        if (res.data.status === 200) {
            return res.data.data;
        } else {
            throw new Error(res.data.message || 'Failed to process catalog CSV file.');
        }
    } catch (error) {
        console.error('Error processing catalog CSV:', error);
        throw new Error(error.response?.data?.message || 'Failed to process catalog CSV file. Please try again.');
    }
};

/**
 * Auto-detect CSV type based on column headers
 * @param {File} file - The CSV file to analyze
 * @returns {Promise<string>} - Promise that resolves to 'booking', 'catalog', or 'unknown'
 */
export const detectCSVType = (file) => {
    return new Promise((resolve, reject) => {
        const fileText = file.text();
        
        fileText.then(text => {
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                preview: 1, // Only read first row to get headers
                complete: (results) => {
                    if (results.data.length === 0) {
                        resolve('unknown');
                        return;
                    }
                    
                    const headers = Object.keys(results.data[0]).map(h => h.toLowerCase());
                    
                    // Booking indicators
                    const bookingIndicators = [
                        'perf.date', 'perf date', 'performance date', 'show date', 'venue',
                        'city', 'state', 'guarantee', 'walkout', 'ticket', 'promoter'
                    ];
                    
                    // Catalog indicators
                    const catalogIndicators = [
                        'song title', 'song', 'artist', 'splits', 'master splits',
                        'publishing splits', 'release date', 'album', 'track'
                    ];
                    
                    const bookingMatches = headers.filter(h => 
                        bookingIndicators.some(indicator => h.includes(indicator))
                    ).length;
                    
                    const catalogMatches = headers.filter(h => 
                        catalogIndicators.some(indicator => h.includes(indicator))
                    ).length;
                    
                    if (bookingMatches > catalogMatches && bookingMatches >= 2) {
                        resolve('booking');
                    } else if (catalogMatches > bookingMatches && catalogMatches >= 2) {
                        resolve('catalog');
                    } else {
                        resolve('unknown');
                    }
                },
                error: (error) => {
                    console.error('CSV detection error:', error);
                    resolve('unknown');
                }
            });
        }).catch(error => {
            console.error('Error reading file for detection:', error);
            resolve('unknown');
        });
    });
};

